
import { useState, useRef, useEffect } from "react";
import { SendHorizonal, Plus, X, Bot, User, Mic, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/hooks/use-toast";

interface Message {
  id: string;
  content: string;
  sender: "user" | "assistant";
  timestamp: Date;
}

interface Credits {
  used: number;
  limit: number;
}

export function ChatAssistant() {
  const [isOpen, setIsOpen] = useState(true);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      content: "Hello! I'm Vita, your AI wellness assistant for Vitatee. Create an account today to get personalized guidance and start your wellness journey with me! I can help with nutrition, fitness, supplements, and mindfulness.",
      sender: "assistant",
      timestamp: new Date(),
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [credits, setCredits] = useState<Credits | null>(null);
  const [showThinking, setShowThinking] = useState(false);

  const { user } = useAuth();
  const navigate = useNavigate();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const fetchCredits = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('vita_credits')
        .select('credits_used, credits_limit')
        .eq('user_id', user.id)
        .single();
        
      if (error) throw error;
      
      if (data) {
        setCredits({
          used: data.credits_used,
          limit: data.credits_limit
        });
        console.log("Fetched credits:", data);
      }
    } catch (error) {
      console.error("Error fetching credit usage:", error);
    }
  };

  useEffect(() => {
    fetchCredits();

    // Set up realtime subscription for credits
    if (user) {
      const channel = supabase
        .channel('vita-credits-changes')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'vita_credits',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            console.log('Credits updated:', payload);
            if (payload.new) {
              setCredits({
                used: payload.new.credits_used,
                limit: payload.new.credits_limit
              });
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, showThinking]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;
    
    setError(null);
    
    if (!user) {
      setError("Please sign up or log in to chat with Vita, your AI wellness assistant!");
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      content: input,
      sender: "user",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);
    setShowThinking(true);

    try {
      // Format messages for API
      const messageHistory = messages.map(msg => ({
        role: msg.sender === "user" ? "user" : "assistant",
        content: msg.content
      }));
      
      // Add the current user message
      messageHistory.push({
        role: "user",
        content: input
      });

      console.log("Sending messages to chat-vita:", JSON.stringify(messageHistory));

      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error("You must be logged in to use Vita");
      }

      const { data, error } = await supabase.functions.invoke('chat-vita', {
        body: { messages: messageHistory },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      console.log("Response from chat-vita:", data, error);

      if (error) {
        console.error('Error calling chat function:', error);
        throw new Error(error.message || "Error connecting to Vita");
      }

      if (data.error) {
        console.error('Error in chat response:', data.error);
        throw new Error(data.error);
      }

      if (data.credits) {
        setCredits(data.credits);
        console.log("Updated credits from response:", data.credits);
      }

      const responseMessage: Message = {
        id: Date.now().toString(),
        content: data.response,
        sender: "assistant",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, responseMessage]);
    } catch (error: any) {
      console.error('Error in chat:', error);
      
      if (error.message.includes('credit limit') || error.message.includes('subscription')) {
        setError(error.message);
      } else if (error.message.includes('authentication') || error.message.includes('log in')) {
        setError("Please sign up or log in to chat with Vita!");
      } else {
        setError("I'm sorry, I had trouble processing that request. Please try again later.");
        toast({
          title: "Chat Error",
          description: error.message || "Error connecting to Vita",
          type: "error"
        });
      }
    } finally {
      setIsLoading(false);
      setShowThinking(false);
      // Fetch the latest credits after sending a message
      if (user) {
        fetchCredits();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  const handleSignUpClick = () => {
    navigate("/onboarding");
    setIsOpen(false);
  };

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        className={cn(
          "fixed bottom-6 right-6 rounded-full shadow-lg transition-all duration-300",
          "h-14 w-14 p-0 flex items-center justify-center",
          isOpen ? "scale-0 opacity-0" : "scale-100 opacity-100"
        )}
      >
        <Bot className="h-6 w-6" />
      </Button>

      <div
        className={cn(
          "fixed bottom-6 right-6 w-80 sm:w-96 rounded-2xl shadow-xl glass-card overflow-hidden z-50",
          "transition-all duration-300 transform origin-bottom-right",
          isOpen
            ? "scale-100 opacity-100 translate-y-0"
            : "scale-90 opacity-0 translate-y-4 pointer-events-none"
        )}
        style={{ maxHeight: "calc(100vh - 100px)" }}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center">
            <Avatar className="h-8 w-8 mr-2">
              <Bot className="h-4 w-4" />
            </Avatar>
            <div>
              <h3 className="text-sm font-medium">Vita</h3>
              <Badge variant="outline" className="text-xs font-normal text-emerald-600 bg-emerald-50">
                AI Wellness Assistant
              </Badge>
            </div>
          </div>
          {user && credits && (
            <div className="flex flex-col items-center mr-2">
              <p className="text-xs text-muted-foreground">
                {credits.used}/{credits.limit} credits
              </p>
              <Progress 
                value={(credits.used / credits.limit) * 100} 
                className="h-1 w-16" 
              />
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full"
            onClick={() => setIsOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-4 max-h-[400px] overflow-y-auto">
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Access Limited</AlertTitle>
              <AlertDescription>
                {error}
                {!user && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-2 w-full"
                    onClick={handleSignUpClick}
                  >
                    Sign Up for Free Trial
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          )}
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  "flex items-start space-x-2",
                  message.sender === "user" ? "justify-end" : "justify-start"
                )}
              >
                {message.sender === "assistant" && (
                  <Avatar className="h-8 w-8 mt-1">
                    <Bot className="h-4 w-4" />
                  </Avatar>
                )}
                <div
                  className={cn(
                    "max-w-[75%] p-3 rounded-lg",
                    message.sender === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary"
                  )}
                >
                  <p className="text-sm">{message.content}</p>
                  <p className="text-xs opacity-70 mt-1">
                    {message.timestamp.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
                {message.sender === "user" && (
                  <Avatar className="h-8 w-8 mt-1">
                    <User className="h-4 w-4" />
                  </Avatar>
                )}
              </div>
            ))}
            
            {/* Thinking indicator */}
            {showThinking && (
              <div className="flex items-start space-x-2 justify-start">
                <Avatar className="h-8 w-8 mt-1">
                  <Bot className="h-4 w-4" />
                </Avatar>
                <div className="max-w-[75%] p-3 rounded-lg bg-secondary">
                  <div className="flex items-center space-x-2">
                    <div className="h-2 w-2 bg-primary rounded-full animate-pulse"></div>
                    <div className="h-2 w-2 bg-primary rounded-full animate-pulse delay-150"></div>
                    <div className="h-2 w-2 bg-primary rounded-full animate-pulse delay-300"></div>
                    <span className="text-sm ml-1">Vita is thinking...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </div>

        <div className="p-4 border-t">
          <div className="flex items-end">
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full mr-1">
              <Plus className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full mr-2">
              <Mic className="h-4 w-4" />
            </Button>
            <div className="flex-1 relative">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={user ? "Ask Vita anything..." : "Sign up to chat with Vita..."}
                className="min-h-[44px] resize-none border border-input focus-visible:ring-1"
                rows={1}
                disabled={isLoading || showThinking || !user}
              />
              {isLoading && !showThinking && (
                <div className="absolute right-3 top-3">
                  <div className="h-4 w-4 border-2 border-t-primary rounded-full animate-spin"></div>
                </div>
              )}
            </div>
            {user ? (
              <Button
                className="ml-2 p-2 h-10 w-10 rounded-full"
                disabled={!input.trim() || isLoading || showThinking}
                onClick={handleSendMessage}
              >
                <SendHorizonal className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                className="ml-2"
                onClick={handleSignUpClick}
              >
                Sign Up
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
